<template>
    <!-- a frame of darker wood around the court, so the shots on the lines are whole -->
    <div class="shot-court" :class="{ 'shot-court--zones': mode === 'zones' }" :style="{ '--dot-size': `${dotSize}px` }">
        <div class="shot-court__area">
            <court-floor />
            <!-- zone areas, coloured by how well the team shot from there -->
            <svg v-if="mode === 'zones'" class="shot-court__zones" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                    <mask :id="maskId" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100">
                        <rect x="0" y="0" width="100" height="100" fill="#fff" />
                        <path :d="paintPath" fill="#000" />
                    </mask>
                </defs>
                <path
                    v-for="zone in zones"
                    :key="zone.id"
                    :d="zone.path"
                    :mask="zone.masked ? `url(#${maskId})` : null"
                    class="zone-area"
                    :class="`zone-area--${zone.level}`"
                />
                <!-- borders drawn on top, so every zone is outlined fully -->
                <path
                    v-for="zone in zones"
                    :key="`line-${zone.id}`"
                    :d="zone.path"
                    :mask="zone.masked ? `url(#${maskId})` : null"
                    class="zone-line"
                    vector-effect="non-scaling-stroke"
                />
            </svg>

            <template v-if="mode === 'zones'">
                <div
                    v-for="zone in zones"
                    :key="`badge-${zone.id}`"
                    class="zone-badge"
                    :class="`zone-badge--${zone.level}`"
                    :style="{ left: `${zone.at[0]}%`, top: `${zone.at[1]}%` }"
                >
                    <info-tip :text="zone.label" block>
                        <b>{{ zone.made }}/{{ zone.attempts }}</b>
                        <small>{{ zone.attempts ? zone.pct : '—' }}</small>
                    </info-tip>
                </div>
            </template>
            <template v-else>
                <div
                    v-for="shot in shots"
                    :key="shot.id"
                    class="shot-dot"
                    :class="{ 'shot-dot--made': shot.made }"
                    :style="{ left: `${shot.x * 100}%`, top: `${shot.y * 100}%`, borderColor: shot.color, backgroundColor: shot.made ? shot.color : '#fff' }"
                >
                    <info-tip :text="shotTitle(shot)" block />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import CourtFloor from '@/components/CourtFloor'
    import { ZONES, PAINT_PATH, getShotZone, getZoneLevel, toPercentLabel, periodLabel } from '@/utils/shotChart'

    // A half court with the shots of one team, as dots or as zones
    export default {
        name: 'ShotCourt',
        components: { CourtFloor },
        props: {
            shots: {
                type: Array,
                default: () => []
            },
            // 'dots' | 'zones'
            mode: {
                type: String,
                default: 'dots'
            },
            dotSize: {
                type: Number,
                default: 16
            }
        },
        data() {
            return {
                paintPath: PAINT_PATH,
                maskId: `shot-court-paint-${this._uid}`
            }
        },
        computed: {
            zones() {
                const stats = {}
                this.shots.forEach((shot) => {
                    const zone = getShotZone(shot)
                    if (!zone) return
                    const entry = stats[zone] || (stats[zone] = { made: 0, attempts: 0 })
                    entry.attempts += 1
                    if (shot.made) entry.made += 1
                })
                return ZONES.map((zone) => {
                    const entry = stats[zone.id] || { made: 0, attempts: 0 }
                    return {
                        ...zone,
                        ...entry,
                        pct: toPercentLabel(entry.made, entry.attempts),
                        level: entry.attempts ? getZoneLevel(zone, entry.made, entry.attempts) : 'empty'
                    }
                })
            }
        },
        methods: {
            shotTitle(shot) {
                const outcome = shot.made ? 'Εύστοχο' : 'Άστοχο'
                const period = shot.period ? ` · ${periodLabel(shot.period)} περίοδος` : ''
                const text = `${outcome} ${shot.points}π${period}`
                return shot.playerName ? `${shot.playerName} · ${text}` : text
            }
        }
    }
</script>

<style scoped>
.shot-court {
    width: 100%;
    padding: 4%;
    border-radius: 12px;
    background:
        linear-gradient(rgba(40, 20, 6, 0.55), rgba(40, 20, 6, 0.55)),
        url('../assets/court-wood.jpg') center / cover;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);
}

.shot-court__area {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
}

.shot-court__zones {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}

.zone-area {
    fill: transparent;
    stroke: none;
}

.zone-area--hot {
    fill: rgba(22, 163, 74, 0.42);
}

.zone-area--ok {
    fill: rgba(245, 158, 11, 0.42);
}

.zone-area--cold {
    fill: rgba(220, 38, 38, 0.42);
}

.zone-area--empty {
    fill: rgba(255, 255, 255, 0.18);
}

.zone-line {
    fill: none;
    stroke: #fff;
    stroke-width: 2;
    stroke-dasharray: 6 4;
}

/* the tip on the badge: the made / attempts over the percentage, as on the badge */
.zone-badge ::v-deep .info-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.shot-dot {
    position: absolute;
    z-index: 2;
    width: var(--dot-size);
    height: var(--dot-size);
    border: calc(var(--dot-size) / 5.5) solid;
    border-radius: 50%;
    transform: translate(-50%, -50%);

    /* a white ring keeps every team colour readable on the parquet */
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9), 0 1px 4px rgba(0, 0, 0, 0.35);
}

.zone-badge {
    position: absolute;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 48px;
    padding: 3px 6px;
    border-radius: 9px;
    background: var(--zone-color);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    color: #fff;
    line-height: 1.1;
    transform: translate(-50%, -50%);
    font-variant-numeric: tabular-nums;
}

.zone-badge b {
    font-size: 0.82rem;
}

.zone-badge small {
    font-size: 0.68rem;
    font-weight: 700;
    opacity: 0.92;
}

.zone-badge--hot {
    --zone-color: #16a34a;
}

.zone-badge--ok {
    --zone-color: #f59e0b;
}

.zone-badge--cold {
    --zone-color: #dc2626;
}

.zone-badge--empty {
    --zone-color: rgba(13, 13, 15, 0.55);
}

@media (max-width: 600px) {
    .zone-badge {
        min-width: 40px;
        padding: 2px 4px;
    }

    .zone-badge b {
        font-size: 0.72rem;
    }

    .zone-badge small {
        font-size: 0.62rem;
    }
}
</style>
